import { useEffect, useState } from "react";
import { Users, UsersThree, House, Clock, ClipboardText, CheckCircle, Flag, XCircle, CurrencyInr, Percent } from "@phosphor-icons/react";
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { StatSkeleton } from "@/components/Loaders";

const COLORS = ["#166534", "#ca8a04", "#0d9488", "#c2410c", "#4d7c0f", "#7c3aed", "#0369a1", "#be123c"];

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);

  const stats = d
    ? [
        { label: "Total Customers", value: d.total_customers, icon: Users },
        { label: "Total Owners", value: d.total_owners, icon: UsersThree },
        { label: "Total Properties", value: d.total_properties, icon: House },
        { label: "Pending Properties", value: d.pending_properties, icon: Clock },
        { label: "Total Bookings", value: d.total_bookings, icon: ClipboardText },
        { label: "Pending Bookings", value: d.pending_bookings, icon: Clock },
        { label: "Confirmed Bookings", value: d.confirmed_bookings, icon: CheckCircle },
        { label: "Completed Bookings", value: d.completed_bookings, icon: Flag },
        { label: "Cancelled / Rejected", value: d.cancelled_bookings, icon: XCircle },
        { label: "Total Booking Value", value: formatINR(d.total_booking_value), icon: CurrencyInr },
        { label: "Total Avinya Commission", value: formatINR(d.total_commission), icon: Percent },
      ]
    : [];

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Platform Dashboard</h1>
      <p className="mt-1 text-sm text-stone-500">Commission rate: {d ? `${d.commission_rate}%` : "—"}</p>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {!d
          ? [1, 2, 3, 4].map((i) => <StatSkeleton key={i} />)
          : stats.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} data-testid={`admin-stat-${s.label}`} className="rounded-2xl border border-stone-200 bg-white p-5">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-wide text-stone-400">{s.label}</p>
                    <Icon size={18} weight="duotone" className="text-primary" />
                  </div>
                  <p className="mt-2 font-heading text-xl font-bold text-stone-900 tabular-nums">{s.value}</p>
                </div>
              );
            })}
      </div>

      {d && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="mb-4 font-heading font-semibold">Bookings per Month</h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.chart_monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0efed" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
                <Tooltip /><Bar dataKey="bookings" fill="#166534" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-6">
            <h2 className="mb-4 font-heading font-semibold">Revenue & Commission</h2>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={d.chart_monthly}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#166534" stopOpacity={0.3} /><stop offset="95%" stopColor="#166534" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0efed" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
                <Tooltip /><Area type="monotone" dataKey="revenue" stroke="#166534" fill="url(#rev)" /><Area type="monotone" dataKey="commission" stroke="#ca8a04" fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-6 lg:col-span-2">
            <h2 className="mb-4 font-heading font-semibold">Properties by Category</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.chart_categories} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                  {d.chart_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
